/**
 * How a person is displayed when we cannot simply show their name.
 *
 * Before this existed, every surface invented its own fallback and they were
 * all the same wrong answer. The chat panel rendered "Inconnu", the audition
 * loaders a hardcoded English 'Unknown', the partner candidate list dropped
 * the row entirely, and `${firstName} ${lastName ?? ''}` printed the literal
 * string "null" for anyone without a first name.
 *
 * The reason one fallback cannot serve is that these are DIFFERENT FACTS:
 *
 *   deleted     the account was deleted; the retained name is still shown,
 *               marked, because an organisation needs to know who it made an
 *               offer to
 *   unclaimed   a profile created by data import that no auth user ever
 *               claimed — 1 803 of these exist, 917 with audition applications
 *   restricted  the row exists but the CALLER's RLS scope excludes it. Saying
 *               "unknown" here asserts something false about the person; the
 *               honest answer is that we are not allowed to look
 *   merged      folded into another profile by the merge engine
 *   unnamed     readable, but no name recorded
 *
 * `restricted` is the one that must never be collapsed into the others. The
 * comment at get-applications.ts:160-163 is the precedent: that surface
 * deliberately escalated to an admin client because "Unknown" was a *false*
 * answer there. Where we cannot legitimately escalate, "restricted" is the
 * true one — so this returns `displayName: null` for it, and callers render a
 * lock rather than inventing a name.
 *
 * Deliberately pure: no I/O, no React, no Supabase. It has to be usable in a
 * client component, a server component, and a CSV exporter alike.
 */

export type IdentityStatus =
  | 'present'
  | 'deleted'
  | 'unclaimed'
  | 'restricted'
  | 'merged'
  | 'unnamed';

export interface IdentityState {
  status: IdentityStatus;
  /** The name to show, or null when showing one would be a lie. */
  displayName: string | null;
  /** Avatar initials. Empty when there is no name we may show. */
  initials: string;
  /** i18n key for the label that replaces or annotates the name. */
  labelKey: string;
  /** Whether the surface should render a status pill next to the name. */
  showBadge: boolean;
}

export interface IdentityProfileInput {
  first_name?: string | null;
  last_name?: string | null;
  deleted_at?: string | null;
  is_deleted?: boolean | null;
  auth_user_id?: string | null;
  is_merged?: boolean | null;
}

export interface ResolveIdentityInput {
  /** The id the record points at, if the record carries one. */
  userId?: string | null;
  /** The profile row, if the caller could read it. */
  profile?: IdentityProfileInput | null;
  /**
   * Did the caller's own (RLS-scoped) read include this id?
   *
   * This is the discriminator between "gone" and "not allowed to look", and
   * it is knowledge only the caller has — which is why it is a parameter and
   * not something this function can infer.
   */
  inReadScope?: boolean;
}

const LABEL_KEYS: Record<IdentityStatus, string> = {
  present: 'common:identity.present',
  deleted: 'common:identity.deleted',
  unclaimed: 'common:identity.unclaimed',
  restricted: 'common:identity.restricted',
  merged: 'common:identity.merged',
  unnamed: 'common:identity.unnamed',
};

function joinName(profile: IdentityProfileInput | null | undefined) {
  return (
    [profile?.first_name, profile?.last_name]
      .filter((part): part is string => Boolean(part && part.trim()))
      .join(' ')
      .trim() || null
  );
}

export function identityInitials(displayName: string | null): string {
  if (!displayName) return '';

  return displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

/**
 * Decide, in order. The ordering is the contract: a deleted profile that also
 * has no name is `deleted`, not `unnamed`, because why it is unnamed is the
 * more useful fact.
 */
export function resolveIdentity(input: ResolveIdentityInput): IdentityState {
  const { profile, inReadScope = true } = input;

  const build = (
    status: IdentityStatus,
    displayName: string | null,
    showBadge: boolean,
  ): IdentityState => ({
    status,
    displayName,
    initials: identityInitials(displayName),
    labelKey: LABEL_KEYS[status],
    showBadge,
  });

  if (!profile) {
    // The reference outlived the row we can see. Whether that means "gone" or
    // "not ours to read" is the caller's knowledge, not ours to guess.
    return inReadScope
      ? build('deleted', null, true)
      : build('restricted', null, true);
  }

  if (profile.is_merged) {
    return build('merged', joinName(profile), true);
  }

  if (profile.is_deleted || profile.deleted_at) {
    // The name is deliberately retained on a tombstone and deliberately shown.
    return build('deleted', joinName(profile), true);
  }

  const name = joinName(profile);

  if (!name) {
    // No auth user ever claimed this row: an import, not a departure.
    return profile.auth_user_id === null
      ? build('unclaimed', null, true)
      : build('unnamed', null, true);
  }

  return build('present', name, false);
}

/**
 * A plain string for a non-React surface: a CSV, a PDF, a cast sheet, an email.
 *
 * Those callers have no `Trans` and often no i18n instance, and were each
 * inventing their own fallback — a hardcoded English 'Unknown' that appeared
 * untranslated in a French document, and said nothing about WHY the name was
 * missing.
 *
 * A deleted account keeps its retained name and is marked, so an export names
 * the person and states their status in one string. Pass `t` where an i18n
 * instance exists; without one it degrades to English rather than refusing.
 */
export function identityToPlainString(
  identity: IdentityState,
  t?: (key: string) => string,
): string {
  const label = t ? t(identity.labelKey) : PLAIN_LABELS[identity.status];

  if (identity.status === 'present') {
    return identity.displayName ?? label;
  }

  // Name AND status, not one or the other: a cast sheet that says only
  // "account deleted" has lost the person it was about.
  return identity.displayName ? `${identity.displayName} (${label})` : label;
}

const PLAIN_LABELS: Record<IdentityStatus, string> = {
  present: '',
  deleted: 'account deleted',
  unclaimed: 'unclaimed profile',
  restricted: 'restricted access',
  merged: 'merged account',
  unnamed: 'unnamed',
};
